import {
  Banner,
  BlockStack,
  Icon,
  InlineStack,
  LegacyStack,
  Pagination,
  ResourceList,
  Text,
  TextField,
  Thumbnail,
} from '@shopify/polaris'
import { SearchIcon } from '@shopify/polaris-icons'
import React, { useCallback, useEffect, useState } from 'react'
import useSWR from 'swr'

import { AdaptiveModal } from './AdaptiveModal'
import { backendFetchResult } from './api'
import { GQLPaginator, GQLPaginatorPageInfo } from './gql-models-common'
import { queryString, useDebounced } from './helpers'
import { useSessionStorage } from './session-storage'

export interface ProductVariantResult {
  id: string
  restId: number
  title: string
  price: string
  productTitle: string
  variantTitle: string | null
  image: string | null
  requiresShipping: boolean
}

interface ProductVariantQueryResult {
  search: string
  results: ProductVariantResult[]
  pageInfo: GQLPaginatorPageInfo | null
  beforeCursor: string | null
  afterCursor: string | null
}

const searchProductVariants = async (
  _: any,
  search: string,
  currency: string,
  beforeCursor: string | null,
  afterCursor: string | null
): Promise<ProductVariantQueryResult> => {
  const query = queryString({
    search,
    currency,
    limit: 30,
    before: beforeCursor,
    after: afterCursor,
  })
  const res = await backendFetchResult<GQLPaginator<ProductVariantResult>>(
    'GET',
    `/products/search-variants?${query}`
  )
  const results = res.status === 'success' && res.data ? res.data.nodes : []
  const pageInfo = res.data?.page ?? null
  return { search, beforeCursor, afterCursor, results, pageInfo }
}

const renderProductVariantResult = (
  result: ProductVariantResult,
  onSelect: (result: ProductVariantResult) => any
) => {
  return (
    <ResourceList.Item
      id={result.id}
      media={<Thumbnail source={result.image || ''} alt="" size="small" />}
      onClick={() => onSelect(result)}
      verticalAlignment="center"
    >
      <h3>
        <Text as="span" fontWeight="semibold">
          {result.productTitle}
        </Text>
        {result.variantTitle && <p>{result.variantTitle}</p>}
      </h3>
    </ResourceList.Item>
  )
}

interface ModalContentProps {
  currency: string
  search: string
  onSearchChange: (value: string) => void
  items: ProductVariantResult[]
  loading: boolean
  renderItem: (item: ProductVariantResult) => React.ReactNode
}

const ModalContent = ({
  currency,
  search,
  onSearchChange,
  items,
  loading,
  renderItem,
}: ModalContentProps) => {
  const [dismissed, setDismissed] = useSessionStorage<boolean>(
    'product-variant-currency-banner-dismissed',
    false
  )

  return (
    <>
      <AdaptiveModal.Section>
        <BlockStack gap="200">
          <TextField
            label=""
            placeholder="Search"
            prefix={<Icon source={SearchIcon} />}
            value={search}
            onChange={onSearchChange}
            autoComplete="off"
          />
          {!dismissed && (
            <Banner tone="info" onDismiss={() => setDismissed(true)}>
              Only products available in <Text as="strong">{currency}</Text> currency are being
              displayed.
            </Banner>
          )}
        </BlockStack>
      </AdaptiveModal.Section>
      <ResourceList
        items={items}
        renderItem={renderItem}
        loading={loading}
        emptyState={
          <LegacyStack distribution="center">
            <p style={{ padding: '2rem' }}>No products found</p>
          </LegacyStack>
        }
      />
    </>
  )
}

interface Props {
  currency: string
  open: boolean
  onClose: () => any
  onSelect: (result: ProductVariantResult) => any
}

export const ProductVariantModal = ({ currency, open, onClose, onSelect }: Props) => {
  const [beforeCursor, setBeforeCursor] = useState<string | null>(null)
  const [afterCursor, setAfterCursor] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounced(search, 500)

  const [queryResult, setQueryResult] = useState<ProductVariantQueryResult>({
    search: '___',
    results: [],
    pageInfo: null,
    beforeCursor: null,
    afterCursor: null,
  })
  const swr = useSWR(
    open ? ['product-variants', debouncedSearch, currency, beforeCursor, afterCursor] : null,
    searchProductVariants
  )

  const myRenderProductVariantResult = useCallback(
    (result: ProductVariantResult) => renderProductVariantResult(result, onSelect),
    [onSelect]
  )

  useEffect(() => {
    if (
      swr.data &&
      swr.data.search === search &&
      swr.data.beforeCursor === beforeCursor &&
      swr.data.afterCursor === afterCursor
    ) {
      setQueryResult(swr.data)
    }
  }, [swr.data, search, beforeCursor, afterCursor, setQueryResult])

  useEffect(() => {
    setBeforeCursor(null)
    setAfterCursor(null)
  }, [search])

  const loading =
    queryResult.search !== search ||
    queryResult.beforeCursor !== beforeCursor ||
    queryResult.afterCursor !== afterCursor

  const pagination =
    queryResult.results.length > 0 && queryResult.pageInfo
      ? {
          hasPrevious: queryResult.pageInfo.hasPreviousPage && !loading,
          hasNext: queryResult.pageInfo.hasNextPage && !loading,
          onPrevious: () => {
            setAfterCursor(null)
            setBeforeCursor(queryResult.pageInfo?.firstCursor ?? null)
          },
          onNext: () => {
            setBeforeCursor(null)
            setAfterCursor(queryResult.pageInfo?.lastCursor ?? null)
          },
        }
      : undefined

  return (
    <AdaptiveModal
      title="Select product variant"
      open={open}
      onClose={onClose}
      footer={
        pagination && (
          <InlineStack align="center">
            <Pagination
              hasPrevious={pagination.hasPrevious}
              onPrevious={pagination.onPrevious}
              hasNext={pagination.hasNext}
              onNext={pagination.onNext}
            />
          </InlineStack>
        )
      }
    >
      <ModalContent
        currency={currency}
        search={search}
        onSearchChange={setSearch}
        items={queryResult.results}
        loading={loading}
        renderItem={myRenderProductVariantResult}
      />
    </AdaptiveModal>
  )
}
